import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/hooks/use-toast";
import { preferencesService } from "@/services/preferencesService";
import {
  mapPrivacyConsentRecords,
  migrateLegacyPrivacyDismissedToAck,
  PRIVACY_POLICY_VERSION,
  type MappedConsentHistoryItem,
  type PrivacyConsentRecord,
} from "@/lib/privacyConsent";

interface ConsentData {
  hasConsented: boolean;
  consentHistory: MappedConsentHistoryItem[];
}

export type { MappedConsentHistoryItem };

/**
 * Manages user privacy policy consent via the settings API (persisted in `privacy_consent`).
 */
export const usePrivacyConsent = () => {
  const { user } = useAuth();
  const userId = user?.id;
  const [consentData, setConsentData] = useState<ConsentData>({
    hasConsented: false,
    consentHistory: [],
  });
  const [loading, setLoading] = useState(true);

  /** Optional consent-audit location only; browser prompt; denial returns a placeholder (not used for access control). */
  const getCurrentLocation = async (): Promise<string> => {
    try {
      if (typeof navigator === "undefined" || !navigator.geolocation) {
        return "Location not available";
      }

      return await new Promise<string>((resolve) => {
        navigator.geolocation.getCurrentPosition(
          async (position) => {
            try {
              const response = await fetch(
                `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${position.coords.latitude}&longitude=${position.coords.longitude}&localityLanguage=en`
              );
              const data = await response.json();
              resolve(`${data.city}, ${data.countryName}`);
            } catch {
              resolve("Location detected but not identified");
            }
          },
          () => resolve("Location access denied")
        );
      });
    } catch {
      return "Location error";
    }
  };

  const fetchConsentData = useCallback(
    async (options?: { showErrorToast?: boolean }) => {
      if (!userId) {
        setConsentData({ hasConsented: false, consentHistory: [] });
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await preferencesService.fetchPrivacyConsent(userId);
        const records = (data || []) as PrivacyConsentRecord[];

        setConsentData({
          hasConsented: records.length > 0,
          consentHistory: mapPrivacyConsentRecords(records),
        });
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : "Failed to load consent data";
        console.error("Error fetching consent data:", errorMessage);
        if (options?.showErrorToast) {
          toast({
            title: "Error loading consent data",
            description: errorMessage,
            variant: "destructive",
          });
        }
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  /**
   * Give Consent.
   */
  const giveConsent = async (options?: { showSuccessToast?: boolean }) => {
    if (!userId) {
      return false;
    }

    const showSuccessToast = options?.showSuccessToast !== false;

    try {
      const location = await getCurrentLocation();
      const ipResponse = await fetch("https://api.ipify.org?format=json");
      const ipData = (await ipResponse.json()) as { ip?: string };
      const [city, country] = location.split(", ");

      await preferencesService.createPrivacyConsent({
        user_id: userId,
        consent_date: new Date().toISOString(),
        consent_type: "privacy_policy",
        ip_address: ipData.ip,
        location: {
          city: city || "Unknown",
          country: country || "Unknown",
          full: location,
        },
        metadata: { policy_version: PRIVACY_POLICY_VERSION },
      });

      if (showSuccessToast) {
        toast({
          title: "Privacy Policy Accepted",
          description: "Your consent has been recorded successfully.",
        });
      }

      await fetchConsentData();
      return true;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to record consent";
      console.error("Error recording consent:", errorMessage);
      toast({
        title: "Error recording consent",
        description: errorMessage,
        variant: "destructive",
      });
      return false;
    }
  };

  useEffect(() => {
    migrateLegacyPrivacyDismissedToAck();
  }, []);

  useEffect(() => {
    void fetchConsentData();
  }, [fetchConsentData]);

  return {
    consentData,
    loading,
    giveConsent,
    refreshConsent: (showErrorToast?: boolean) => fetchConsentData({ showErrorToast }),
  };
};
